import { useCallback, useEffect, useState } from 'react';
import { DuplexerEventPayload } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { IHttpClient } from '@wix/http-client';
import { AssistantChatEntity, usePocDuplexer } from './useDuplexer';

export interface AssistantMessage {
  role: 'user' | 'assistant' | 'thinking';
  content: string;
  timestamp: string;
}

interface AssistantResponse {
  messages: AssistantMessage[];
  requestId: string;
  taskCreated?: { id: string; title: string };
}

export function usePocChat(httpClient: IHttpClient, signedInstance: string) {
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastTaskCreated, setLastTaskCreated] = useState<{ id: string; title: string } | null>(null);

  const handleDuplexerEvent = useCallback((payload: DuplexerEventPayload<AssistantChatEntity>) => {
    const entity = payload?.entity;
    if (!entity?.content) {
      return;
    }

    setMessages((prev) => [...prev, { role: entity.role, content: entity.content, timestamp: new Date().toISOString() }]);

    if (entity.taskCreated) {
      setLastTaskCreated(entity.taskCreated);
    }

    if (entity.done) {
      setIsLoading(false);
    }
  }, []);

  usePocDuplexer(handleDuplexerEvent, signedInstance);

  // Load existing conversation on mount
  useEffect(() => {
    httpClient
      .get<{ messages: AssistantMessage[] }>('/v1/conversation')
      .then(({ data }) => {
        if (data.messages?.length) {
          setMessages(data.messages);
        }
      })
      .catch(() => {
        // Endpoint not deployed yet — fail gracefully
      });
  }, [httpClient]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) {
        return;
      }

      setError(null);
      setIsLoading(true);
      setMessages((prev) => [...prev, { role: 'user', content, timestamp: new Date().toISOString() }]);

      try {
        const { data } = await httpClient.post<AssistantResponse>('/v1/chat', { message: content });

        // POST response is authoritative — replace Duplexer-streamed messages
        setMessages(data.messages);

        if (data.taskCreated) {
          setLastTaskCreated(data.taskCreated);
        }
      } catch (err: any) {
        const errorMsg = err?.response?.data?.error || err.message || 'Something went wrong';
        setError(errorMsg);
        setMessages((prev) => [...prev, { role: 'assistant', content: `Error: ${errorMsg}`, timestamp: new Date().toISOString() }]);
      } finally {
        setIsLoading(false);
      }
    },
    [httpClient, isLoading],
  );

  const clearConversation = useCallback(async () => {
    try {
      await httpClient.post('/v1/clear');
      setMessages([]);
      setLastTaskCreated(null);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  }, [httpClient]);

  return { messages, isLoading, error, lastTaskCreated, sendMessage, clearConversation };
}
